import React from 'react';
import { DollarSign } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Card, CardContent } from '../../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { useUserPreferences } from '../../context';
import { MetadataBadge } from './MetadataBadge';

// ============================================================================
// KanbanCard - Main card component
// ============================================================================

export interface KanbanCardProps {
  /** Unique identifier for the card */
  id: string;
  /** Primary title text */
  title: string;
  /** Secondary subtitle text (e.g., company name) */
  subtitle?: string;
  /** Whether the card is draggable (HTML5 native) */
  draggable?: boolean;
  /** Drag start handler (HTML5 native) */
  onDragStart?: (e: React.DragEvent) => void;
  /** Click handler */
  onClick?: () => void;
  /** Additional class names */
  className?: string;
  /** Card content sections */
  children?: React.ReactNode;
  /** Optional drag handle element (rendered on the left side) */
  dragHandle?: React.ReactNode;
  /** Optional inline style (for dnd-kit transforms) */
  style?: React.CSSProperties;
  /** Extra HTML attributes to spread on the card (e.g. dnd-kit attributes) */
  cardAttributes?: React.HTMLAttributes<HTMLDivElement>;
}

export const KanbanCard = React.forwardRef<HTMLDivElement, KanbanCardProps>(
  function KanbanCard(
    {
      id,
      title,
      subtitle,
      draggable,
      onDragStart,
      onClick,
      className,
      children,
      dragHandle,
      style,
      cardAttributes,
    },
    ref,
  ) {
    return (
      <Card
        ref={ref}
        data-card-id={id}
        draggable={draggable}
        onDragStart={onDragStart}
        onClick={onClick}
        style={style}
        {...cardAttributes}
        className={cn(
          'mb-2 cursor-pointer transition-shadow',
          'shadow-sm hover:shadow-md',
          draggable && 'cursor-grab active:cursor-grabbing',
          className
        )}
      >
        <CardContent className="p-3">
          <div className="flex gap-2">
            {dragHandle && (
              <div className="flex items-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                {dragHandle}
              </div>
            )}
            <div className="flex-1 min-w-0">
              {/* Title & Subtitle */}
              <div className="mb-2">
                <p className="text-sm font-medium truncate">{title}</p>
                {subtitle && (
                  <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
                )}
              </div>

              {/* Custom content sections */}
              {children}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  },
);

// ============================================================================
// CardContactRow - Avatar + Name display
// ============================================================================

export interface CardContactRowProps {
  /** Avatar configuration */
  avatar?: {
    /** Initials to display in fallback */
    initials?: string;
    /** Image URL */
    src?: string;
  };
  /** Contact name */
  name?: string;
  /** Additional class names */
  className?: string;
}

export function CardContactRow({ avatar, name, className }: CardContactRowProps) {
  if (!name && !avatar?.initials) return null;

  return (
    <div className={cn('flex items-center gap-2 mb-2', className)}>
      <Avatar className="h-6 w-6 text-[10px]">
        {avatar?.src && <AvatarImage src={avatar.src} alt={name} />}
        <AvatarFallback className="bg-muted text-muted-foreground">
          {avatar?.initials || name?.charAt(0)?.toUpperCase() || '?'}
        </AvatarFallback>
      </Avatar>
      {name && <span className="text-xs text-muted-foreground truncate">{name}</span>}
    </div>
  );
}

// ============================================================================
// CardAmount - Currency amount display
// ============================================================================

export interface CardAmountProps {
  /** Amount in minor units (cents) or major units based on isCents */
  amount?: number | null;
  /** Currency code (e.g., "USD") */
  currency?: string;
  /** Whether to show dollar/currency icon */
  showIcon?: boolean;
  /** Whether amount is in cents (will divide by 100) */
  isCents?: boolean;
  /** Additional class names */
  className?: string;
}

export function CardAmount({
  amount,
  currency = 'USD',
  showIcon = true,
  isCents = true,
  className,
}: CardAmountProps) {
  const { formatCurrency } = useUserPreferences();

  if (amount === null || amount === undefined) return null;

  const displayAmount = isCents ? amount / 100 : amount;
  const formattedAmount = formatCurrency(displayAmount, currency);

  return (
    <div className={cn('flex items-center gap-1', className)}>
      {showIcon && <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />}
      <span className="text-sm font-semibold text-primary">{formattedAmount}</span>
    </div>
  );
}

// ============================================================================
// CardMetadataRow - Icon counts + optional assignee
// ============================================================================

export interface MetadataItem {
  /** Icon element */
  icon: React.ReactNode;
  /** Value to display */
  value: number | string;
  /** Tooltip text */
  tooltip?: string;
  /** Click handler - if provided, badge becomes interactive with hover state */
  onClick?: (e: React.MouseEvent) => void;
}

export interface CardMetadataRowProps {
  /** Array of metadata items to display */
  items?: MetadataItem[];
  /** Assignee to display on the right */
  assignee?: {
    avatar?: {
      initials?: string;
      src?: string;
    };
    name?: string;
  };
  /** Additional class names */
  className?: string;
}

export function CardMetadataRow({ items, assignee, className }: CardMetadataRowProps) {
  // Don't render if nothing to show
  if ((!items || items.length === 0) && !assignee?.name) return null;

  return (
    <div className={cn('flex items-center justify-between mt-2 pt-2 border-t', className)}>
      {/* Metadata items */}
      <div className="flex items-center gap-2">
        {items?.map((item, index) => (
          <MetadataBadge
            key={index}
            icon={item.icon}
            value={item.value}
            tooltip={item.tooltip}
            onClick={item.onClick}
          />
        ))}
      </div>

      {/* Assignee */}
      {assignee?.name && (
        <div className="flex items-center gap-1.5">
          <Avatar className="h-5 w-5 text-[9px]">
            {assignee.avatar?.src && (
              <AvatarImage src={assignee.avatar.src} alt={assignee.name} />
            )}
            <AvatarFallback className="bg-muted text-muted-foreground">
              {assignee.avatar?.initials || assignee.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="text-xs text-muted-foreground truncate max-w-[80px]">
            {assignee.name}
          </span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Utility: Get initials from a name
// ============================================================================

export function getInitials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}
